import { type ArticleDto, articleSchema } from "@modules/article/gateway/articleApiDto"
import { type ArticleApiGateway } from "@modules/article/gateway/articleApiGateway"
import { SanityService } from "@modules/common/cms/sanityService"
import { LoggerService } from "@modules/common/logger/loggerService"
import { i18n } from "@modules/core/utils/i18n"

export class ArticleApi implements ArticleApiGateway {
  private lastCreatedAt?: string
  private lastId?: string
  private lastPage: number = 0

  async getArticles({ page }: { page: number }): Promise<{ page: number; content: ArticleDto[]; hasMore: boolean }> {
    const language = i18n.locale

    if (page === 1) {
      const result = await SanityService.client.fetch<ArticleDto[]>(
        `*[_type == 'article' && language == 'en'] | order(_createdAt desc) [0...10] {
          ...,
          'source_createdAt': _createdAt,
          'source_id': _id,
          ...*[_type == "translation.metadata" && references(^._id)][0].translations[value->language == $language][0].value->{
            ...,
          },
        }`,
        {
          language,
        },
      )

      const lastItem = result[result.length - 1]
      if (result.length === 10 && lastItem) {
        this.lastCreatedAt = lastItem.source_createdAt
        this.lastId = lastItem.source_id
        this.lastPage = 1
      } else {
        this.lastId = "end"
        this.lastPage = 1
      }

      return {
        page,
        content: result.map((articleDto) => LoggerService.parseApiDto(articleSchema, articleDto)),
        hasMore: this.lastId !== "end",
      }
    }

    if (page > 1 && page === this.lastPage + 1) {
      if (this.lastId === "end") {
        return Promise.resolve({ page, content: [], hasMore: false })
      }

      if (this.lastCreatedAt && this.lastId) {
        const result = await SanityService.client.fetch<ArticleDto[]>(
          `*[_type == 'article' && language == 'en' && (_createdAt < $lastCreatedAt || (_createdAt == $lastCreatedAt && _id < $lastId))] | order(_createdAt desc) [0...10] {
            ...,
            'source_createdAt': _createdAt,
            'source_id': _id,
            ...*[_type == "translation.metadata" && references(^._id)][0].translations[value->language == $language][0].value->{
              ...,
            },
          }`,
          {
            lastCreatedAt: this.lastCreatedAt,
            lastId: this.lastId,
            language,
          },
        )

        const lastItem = result[result.length - 1]
        if (result.length === 10 && lastItem) {
          this.lastCreatedAt = lastItem.source_createdAt
          this.lastId = lastItem.source_id
          this.lastPage = page
        } else {
          this.lastId = "end"
          this.lastPage = page
        }

        return {
          page,
          content: result.map((articleDto) => LoggerService.parseApiDto(articleSchema, articleDto)),
          hasMore: this.lastId !== "end",
        }
      }
    }

    return Promise.resolve({ page, content: [], hasMore: false })
  }
}
